import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Button, DataSection, FieldError, FieldRow, Input, Label, sentinel } from '@sentinel/ui';
import { isWeakPassword, mapPasswordViolations } from '@sentinel/auth-core';
import { passwordSchema, type PasswordInput } from '../schema';
import { useChangePassword } from '../use-profile-mutations';
import type { SectionFeedback } from '../types';
import { getBody, getStatus } from '../error-accessors';

export interface PasswordSectionProps {
  managedByIdp: boolean;
  feedback: SectionFeedback;
}

export function PasswordSection({ managedByIdp, feedback }: PasswordSectionProps) {
  const { t } = useTranslation('profile');
  const change = useChangePassword();
  const [done, setDone] = useState(false);
  const form = useForm<PasswordInput>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  async function onSubmit(values: PasswordInput) {
    feedback.clear();
    if (values.new_password === values.current_password) {
      // The backend rejects this with a 400 anyway — catching it locally
      // saves a round trip and a rate-limit hit.
      form.setError('new_password', { message: t('password_same_as_current') });
      return;
    }
    try {
      await change.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      setDone(true);
    } catch (e) {
      const body = getBody(e);
      if (isWeakPassword(body)) {
        const message = mapPasswordViolations(body)
          .map((it) => t(it.i18nKey, { ns: 'common', ...it.params }))
          .join(' ');
        form.setError('new_password', { type: 'policy', message });
        return;
      }
      const status = getStatus(e);
      if (status === 401) {
        form.setError('current_password', { message: t('password_current_incorrect') });
        return;
      }
      if (status === 403) {
        // Unlike the email section, a 403 here is the SSO-only refusal, not a
        // cancelled step-up: this route has no step-up guard, and the
        // middleware only intercepts a 403 whose body is step_up_required.
        // The global forbidden handler reports it, so say nothing here — but
        // keep the branch, or a 403 would fall through to generic_error below
        // and the user would get two messages for one refusal.
        return;
      }
      if (status === 429) {
        // This limiter answers with a plain-text body, so only the status is
        // usable here.
        feedback.error(t('rate_limited'));
        return;
      }
      if (status === 400) {
        // Two domain rejections read as "Something went wrong" otherwise and
        // would send the user back with the same password. Matched on the
        // domain error string the way account.credentials.tsx matches
        // "quota". The two server strings are defined in
        // crates/sentinel-app/src/auth/profile.rs.
        const msg = body?.error?.toLowerCase() ?? '';
        const key = msg.includes('breach')
          ? 'password_breached'
          : msg.includes('recently')
            ? 'password_recently_used'
            : 'generic_error';
        form.setError('new_password', { message: t(key) });
        return;
      }
      feedback.error(t('generic_error'));
    }
  }

  const confirmErrorRaw = form.formState.errors.confirm_password?.message;
  const confirmErrorMsg =
    confirmErrorRaw === 'passwords_must_match' ? t('passwords_must_match') : confirmErrorRaw;
  const currentErrorRaw = form.formState.errors.current_password?.message;
  const currentErrorMsg =
    currentErrorRaw === 'password_required' ? t('password_required') : currentErrorRaw;
  const newErrorRaw = form.formState.errors.new_password?.message;
  const newErrorMsg = newErrorRaw === 'password_required' ? t('password_required') : newErrorRaw;

  if (managedByIdp) {
    return (
      <DataSection testId="profile-password" title={t('password_section_title')}>
        <div data-testid="profile-password-idp-managed" style={{ display: 'grid', gap: '0.5rem' }}>
          <strong>{t('password_idp_managed_title')}</strong>
          <p style={{ margin: 0 }}>{t('password_idp_managed_body')}</p>
          <p style={{ margin: 0, fontSize: 13, color: sentinel.textSubtle }}>
            {t('password_idp_managed_hint')}
          </p>
        </div>
      </DataSection>
    );
  }

  return (
    <DataSection
      testId="profile-password"
      title={t('password_section_title')}
      description={t('password_section_body')}
    >
      {done ? (
        // No automatic redirect: ChangePasswordUseCase already revoked every
        // session including this browser's, so a redirect here would race the
        // revocation and could land the user back on a page that now reads as
        // an error.
        <div
          data-testid="profile-password-done"
          style={{ display: 'grid', gap: '0.5rem', justifyItems: 'start' }}
        >
          <strong>{t('password_done_title')}</strong>
          <p style={{ margin: 0 }}>{t('password_done_body')}</p>
          <Link to="/login" data-testid="profile-password-done-cta">
            {t('password_done_cta')}
          </Link>
        </div>
      ) : (
        <form
          data-testid="profile-password-form"
          onSubmit={form.handleSubmit(onSubmit)}
          style={{ display: 'grid', gap: '0.75rem' }}
        >
          <FieldRow>
            <Label htmlFor="profile-password-current">{t('password_current_label')}</Label>
            <Input
              id="profile-password-current"
              data-testid="profile-password-current"
              type="password"
              autoComplete="current-password"
              aria-invalid={form.formState.errors.current_password ? 'true' : undefined}
              {...form.register('current_password')}
            />
            <FieldError message={currentErrorMsg} />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="profile-password-new">{t('password_new_label')}</Label>
            <Input
              id="profile-password-new"
              data-testid="profile-password-new"
              type="password"
              autoComplete="new-password"
              aria-invalid={form.formState.errors.new_password ? 'true' : undefined}
              {...form.register('new_password')}
            />
            <FieldError message={newErrorMsg} />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="profile-password-confirm">{t('password_confirm_label')}</Label>
            <Input
              id="profile-password-confirm"
              data-testid="profile-password-confirm"
              type="password"
              autoComplete="new-password"
              aria-invalid={form.formState.errors.confirm_password ? 'true' : undefined}
              {...form.register('confirm_password')}
            />
            <FieldError message={confirmErrorMsg} />
          </FieldRow>
          <Button type="submit" data-testid="profile-password-submit" disabled={change.isPending}>
            {t('password_submit')}
          </Button>
        </form>
      )}
    </DataSection>
  );
}
